// eslint-disable @typescript-eslint/no-explicit-any

import React, { useCallback, useEffect, useState } from 'react';
import Datasheet from '@scalartech/react-datasheet';
import { isEmpty } from 'lodash';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import pasteParser from 'components/FeaturedSpreadsheet/utilities/pasteParser';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import ScalarDatasheetProps, { AttributesRendererResponseType } from './ScalarDatasheet.props';
import { EventType, useEventBus } from '../../context';
import CellCountConfig from '../../models/interfaces/cell-count-config.interface';
import { ValueViewer } from '../Spreadsheet/components';

const ScalarDatasheet = ({ valueViewer = ValueViewer, valueRenderer, ...props }: ScalarDatasheetProps) => {
  const [maxAllowedCells, setMaxAllowedCells] = useState<CellCountConfig>({});
  const [selected, setSelected] = useState(null);
  const { clearSelectedFlag, setClearSelected, setSelectedCell, selectedCell } = useFeatureSpreadsheet();
  const { data, virtualization } = props;
  const renderValue = valueRenderer || ((cell: ICell) => cell.value);
  const { publish } = useEventBus();

  useEffect(() => {
    if (clearSelectedFlag) {
      setSelected(null);
      setClearSelected(false);
    }
  }, [clearSelectedFlag, setClearSelected]);

  // any because pasteParser does not have typing configured
  const handleOnPaste = useCallback(clipboard => pasteParser(clipboard, maxAllowedCells as any), [maxAllowedCells]);

  const attributesRenderer = useCallback(
    (cell: ICell): AttributesRendererResponseType => ({
      ...(cell.key ? { id: `CELL-${cell.key}` } : {}),
      'data-parent': cell.parent ?? '',
    }),
    []
  );

  const handleOnSelect = useCallback(
    selection => {
      const totalColumns = data[0]?.length;
      const totalRows = data.length;
      if (selection) {
        const { start } = selection;
        const { i: indexOfRowSelected, j: indexOfColumnSelected } = start;
        setMaxAllowedCells({
          maxAllowedRows: totalRows - indexOfRowSelected,
          maxAllowedColumns: totalColumns - indexOfColumnSelected,
        });
        const isTotalColumn = data[1]?.some(({ isTotal }) => isTotal);
        if (setSelectedCell && !isTotalColumn && !isEmpty(start)) {
          const cellInfo: ICell = data?.[start.i]?.[start.j];
          const { insideLedger, allowInsideLedger } = cellInfo;
          if (!insideLedger || allowInsideLedger) {
            const selectedCellData = {
              ...selection,
              rowNumber: start.i,
              cellInfo,
            };
            publish(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, [selectedCellData, selectedCell]);
            setSelectedCell(selectedCellData);
          }
        }
      }
      setSelected(selection);
    },
    [data, setSelectedCell, publish, selectedCell]
  );

  return (
    <Datasheet
      parsePaste={handleOnPaste}
      onSelect={handleOnSelect}
      selected={selected}
      // any is used because the library does not have typing correctly configured
      // when migrating from js->ts the props incoming to this component are typed
      // but the library has its own typing it expects
      valueRenderer={renderValue as any}
      virtualization={virtualization}
      valueViewer={valueViewer as any}
      attributesRenderer={props.attributesRenderer ?? (attributesRenderer as any)}
      cellRenderer={props.cellRenderer as any}
      dataEditor={props.dataEditor as any}
      onCellsChanged={props.onCellsChanged as any}
      rowRenderer={props.rowRenderer as any}
      data={props.data as any}
      className={props.className as any}
      dataRenderer={props.dataRenderer as any}
      isCellNavigable={props.isCellNavigable as any}
    />
  );
};

export default ScalarDatasheet;
